import { useState } from 'react';
import { comments as mockComments } from '@/mocks/comments';

interface CommentItem {
  id: string;
  user: string;
  date: string;
  rating: number;
  content: string;
}

function Stars({ value }: { value: number }) {
  return (
    <span className="flex items-center gap-0.5 text-primary-500">
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className="w-3.5 h-3.5 flex items-center justify-center">
          <i className={`${n <= value ? 'ri-star-fill' : 'ri-star-line'} text-xs`} />
        </span>
      ))}
    </span>
  );
}

export default function CommentSection({ softwareId }: { softwareId: string }) {
  const initial = mockComments
    .filter((c) => c.softwareId === softwareId)
    .map((c) => ({ id: c.id, user: c.user, date: c.date, rating: c.rating, content: c.content }));

  const [list, setList] = useState<CommentItem[]>(initial);
  const [user, setUser] = useState('');
  const [content, setContent] = useState('');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setNotice('');
    setError('');
    if (!user.trim() || !content.trim()) {
      setError('请填写昵称和评论内容后再发布。');
      return;
    }
    if (content.trim().length > 200) {
      setError('评论内容请控制在 200 字以内。');
      return;
    }
    const today = new Date();
    const date = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(
      today.getDate(),
    ).padStart(2, '0')}`;
    setList((prev) => [
      {
        id: `local-${Date.now()}`,
        user: user.trim(),
        date,
        rating: 5,
        content: content.trim(),
      },
      ...prev,
    ]);
    setUser('');
    setContent('');
    setNotice('评论已发布（演示环境，仅本页显示，不会保存到服务器）。');
  };

  return (
    <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
      <h2 className="font-heading text-lg font-bold text-foreground-950 flex items-center gap-2">
        <span className="w-5 h-5 flex items-center justify-center text-primary-600">
          <i className="ri-chat-3-line text-xl" />
        </span>
        用户评论
        <span className="text-sm font-normal text-foreground-500">（{list.length}）</span>
      </h2>

      <form onSubmit={submit} className="mt-4 rounded-lg bg-background-100 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            value={user}
            onChange={(e) => setUser(e.target.value)}
            placeholder="你的昵称"
            maxLength={20}
            className="h-10 px-3 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 sm:w-48"
          />
          <input
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="分享你的使用体验"
            maxLength={200}
            className="h-10 px-3 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 flex-1"
          />
          <button
            type="submit"
            className="h-10 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-secondary-950 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
          >
            发布评论
          </button>
        </div>
        <p className="mt-2 text-xs text-foreground-400">
          提示：评论为前端演示功能，内容不会保存到服务器。
        </p>
        {error && <p className="mt-2 text-xs text-accent-600">{error}</p>}
        {notice && <p className="mt-2 text-xs text-primary-700">{notice}</p>}
      </form>

      {list.length > 0 ? (
        <ul className="mt-4 flex flex-col divide-y divide-background-200">
          {list.map((c) => (
            <li key={c.id} className="py-4 first:pt-0 flex gap-3">
              <span className="w-9 h-9 rounded-full bg-primary-100 text-primary-800 flex items-center justify-center shrink-0">
                <i className="ri-user-3-line text-base" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-foreground-900">{c.user}</span>
                  <Stars value={c.rating} />
                  <span className="text-xs text-foreground-400">{c.date}</span>
                </div>
                <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed">{c.content}</p>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-4 text-sm text-foreground-500">
          暂无评论，欢迎成为第一个分享体验的用户。
        </p>
      )}
    </section>
  );
}